"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Database = void 0;
const GlobalSQLite_1 = require("../GlobalSQLite");
const exportToJson_1 = require("./ImportExportJson/exportToJson");
const importFromJson_1 = require("./ImportExportJson/importFromJson");
const utilsJson_1 = require("./ImportExportJson/utilsJson");
const utilsJsonEncryption_1 = require("./ImportExportJson/utilsJsonEncryption");
const UtilsSQL92Compatibility_1 = require("./UtilsSQL92Compatibility");
const utilsEncryption_1 = require("./utilsEncryption");
const utilsFile_1 = require("./utilsFile");
const utilsSQLite_1 = require("./utilsSQLite");
const utilsSecret_1 = require("./utilsSecret");
const utilsUpgrade_1 = require("./utilsUpgrade");
class Database {
    constructor(dbName, encrypted, mode, version, isEncryption, readonly, upgDict, globalUtil) {
        this.jsonEncryptUtil = new utilsJsonEncryption_1.UtilsJsonEncryption();
        this.fileUtil = new utilsFile_1.UtilsFile();
        this.sqliteUtil = new utilsSQLite_1.UtilsSQLite();
        this.jsonUtil = new utilsJson_1.UtilsJson();
        this.globalUtil = new GlobalSQLite_1.GlobalSQLite();
        this.encryptionUtil = new utilsEncryption_1.UtilsEncryption();
        this.secretUtil = new utilsSecret_1.UtilsSecret();
        this.upgradeUtil = new utilsUpgrade_1.UtilsUpgrade();
        this.importFromJsonUtil = new importFromJson_1.ImportFromJson();
        this.exportToJsonUtil = new exportToJson_1.ExportToJson();
        this.upgradeVersionDict = {};
        this.sql92Utils = new UtilsSQL92Compatibility_1.UtilsSQL92Compatibility();
        this.dbName = dbName;
        this._encrypted = encrypted;
        this._mode = mode;
        this._isEncryption = isEncryption;
        this.version = version;
        this.readonly = readonly;
        this.upgradeVersionDict = upgDict;
        this.pathDB = this.fileUtil.getFilePath(dbName);
        this._isDbOpen = false;
        this.isTransactionActive = false;
        this.globalUtil = globalUtil ? globalUtil : new GlobalSQLite_1.GlobalSQLite();
        if (this.pathDB.length === 0)
            throw new Error('Could not generate a path to ' + dbName);
        console.log(`&&& Databases path: ${this.pathDB}`);
    }
    /**
     * IsDBOpen
     * return the database status
     * @param options: capSQLiteOptions
     * @returns boolean
     * @since 0.0.1
     */
    isDBOpen() {
        return this._isDbOpen;
    }
    /**
     * Open
     * open the better-sqlite3 database
     * @returns Promise<boolean>
     */
    async open() {
        this._isDbOpen = false;
        let password = '';
        try {
            if (this._encrypted && (this._mode === 'secret' || this._mode === 'encryption' || this._mode === 'decryption')) {
                password = this.secretUtil.getPassphrase();
                if (password.length <= 0) {
                    password = this.globalUtil.secret;
                }
            }
            if (this._mode === 'encryption') {
                await this.encryptionUtil.encryptDatabase(this.pathDB, password);
            }
            if (this._mode === 'decryption') {
                await this.encryptionUtil.decryptDatabase(this.pathDB, password);
                password = '';
            }
            this.database = this.sqliteUtil.openOrCreateDatabase(this.pathDB, password, this.readonly);
            this._isDbOpen = true;
            if (!this.readonly) {
                const curVersion = this.sqliteUtil.getVersion(this.database);
                if (this.version > curVersion && Object.keys(this.upgradeVersionDict).length > 0) {
                    try {
                        await this.fileUtil.copyFileName(this.dbName, `backup-${this.dbName}`);
                        // execute the upgrade flow process
                        await this.upgradeUtil.onUpgrade(this, this.upgradeVersionDict, curVersion, this.version);
                        // delete the backup database
                        await this.fileUtil.deleteFileName(`backup-${this.dbName}`);
                    }
                    catch (err) {
                        // restore the database from backup
                        try {
                            await this.fileUtil.restoreFileName(this.dbName, 'backup');
                        }
                        catch (err) {
                            throw new Error(`Open: ${err}`);
                        }
                    }
                }
            }
            return;
        }
        catch (err) {
            if (this._isDbOpen)
                this.sqliteUtil.closeDB(this.database);
            throw new Error(`Open: ${err}`);
        }
    }
    /**
     * Close
     * close better-sqlite3 database
     * @returns Promise<boolean>
     */
    dbClose() {
        try {
            this.ensureDatabaseIsOpen();
            this.sqliteUtil.closeDB(this.database);
        }
        catch (err) {
            throw new Error(`Close failed: ${this.dbName}  ${err}`);
        }
        finally {
            this._isDbOpen = false;
        }
        return;
    }
    /**
     * IsTransActive
     * Is Database Transaction Active
     * @returns
     */
    isTransActive() {
        return this.isTransactionActive;
    }
    /**
     * SetIsTransActive
     * Set the Database Transaction to Active
     * @returns
     */
    setIsTransActive(value) {
        this.isTransactionActive = value;
    }
    /**
     * DbBeginTransaction
     * Database Begin Transaction
     * @returns
     */
    dbBeginTransaction() {
        try {
            this.ensureDatabaseIsOpen();
            this.sqliteUtil.beginTransaction(this.database, true);
            this.setIsTransActive(true);
            return 0;
        }
        catch (err) {
            throw new Error(`DbBeginTransaction: ${err}`);
        }
    }
    /**
     * DbCommitTransaction
     * Database Commit Transaction
     * @returns
     */
    dbCommitTransaction() {
        try {
            this.ensureDatabaseIsOpen();
            this.sqliteUtil.commitTransaction(this.database, true);
            this.setIsTransActive(false);
            return 0;
        }
        catch (err) {
            throw new Error(`DbCommitTransaction: ${err}`);
        }
    }
    /**
     * DbRollbackTransaction
     * Database Rollback Transaction
     * @returns
     */
    dbRollbackTransaction() {
        try {
            this.ensureDatabaseIsOpen();
            this.sqliteUtil.rollbackTransaction(this.database, true);
            this.setIsTransActive(false);
            return 0;
        }
        catch (err) {
            throw new Error(`DbCommitTransaction: ${err}`);
        }
    }
    /**
     * ChangeSecret
     * open the @journeyapps/sqlcipher sqlite3 database
     * @returns Promise<void>
     */
    async changeSecret() {
        try {
            if (this._mode === 'encryption') {
                // change the password
                const oPassword = this.globalUtil.secret;
                const nPassword = this.globalUtil.newsecret;
                this.sqliteUtil.changePassword(this.pathDB, oPassword, nPassword);
            }
            return;
        }
        catch (err) {
            throw new Error(`Change secret: ${err}`);
        }
    }
    /**
     * GetVersion
     * get the database version
     * @returns Promise<number>
     */
    getVersion() {
        this.ensureDatabaseIsOpen();
        try {
            const currentVersion = this.sqliteUtil.getVersion(this.database);
            return currentVersion;
        }
        catch (err) {
            if (this._isDbOpen)
                this.sqliteUtil.closeDB(this.database);
            throw new Error(`getVersion: ${err}`);
        }
    }
    /**
     * DeleteDB
     * delete a database
     * @param dbName: string
     * @returns Promise<boolean>
     */
    async deleteDB(dbName) {
        // test if file exists
        const isExists = this.fileUtil.isFileExists(dbName);
        if (isExists && !this._isDbOpen) {
            // open the database
            try {
                await this.open();
            }
            catch (err) {
                throw new Error(`DeleteDB: ${err}`);
            }
        }
        // close the database
        try {
            this.dbClose();
        }
        catch (err) {
            throw new Error('DeleteDB: Close failed');
        }
        // delete the database
        if (isExists) {
            try {
                await this.fileUtil.deleteFileName(dbName);
            }
            catch (err) {
                throw new Error(`DeleteDB: deleteFile ${dbName} failed ${err}`);
            }
        }
        return;
    }
    /**
     * IsTableExists
     * @param tableName
     * @returns
     */
    isTableExists(tableName) {
        this.ensureDatabaseIsOpen();
        const isOpen = this._isDbOpen;
        try {
            const tableExistsResult = this.jsonUtil.isTableExists(this.database, isOpen, tableName);
            return tableExistsResult;
        }
        catch (err) {
            throw new Error(`IsTableExists: ${err}`);
        }
    }
    /**
     * CreateSyncTable
     * create the synchronization table
     * @returns Promise<number>
     */
    createSyncTable() {
        this.ensureDatabaseIsOpen();
        let changes = -1;
        const isOpen = this._isDbOpen;
        // check if the table has already being created
        try {
            const retB = this.jsonUtil.isTableExists(this.database, isOpen, 'sync_table');
            if (!retB) {
                const isLastModified = this.sqliteUtil.isLastModified(this.database, isOpen);
                const isSqlDeleted = this.sqliteUtil.isSqlDeleted(this.database, isOpen);
                if (isLastModified && isSqlDeleted) {
                    const date = Math.round(new Date().getTime() / 1000);
                    let stmts = `
                          CREATE TABLE IF NOT EXISTS sync_table (
                              id INTEGER PRIMARY KEY NOT NULL,
                              sync_date INTEGER
                              );`;
                    stmts += `INSERT INTO sync_table (sync_date) VALUES (
                              ${date});`;
                    const results = this.sqliteUtil.execute(this.database, stmts, false, true);
                    changes = results.changes;
                    if (results.changes < 0) {
                        throw new Error(`CreateSyncTable: failed changes < 0`);
                    }
                }
                else {
                    throw new Error('No last_modified/sql_deleted columns in tables');
                }
            }
            else {
                changes = 0;
            }
            return changes;
        }
        catch (err) {
            throw new Error(`CreateSyncTable: ${err}`);
        }
    }
    /**
     * SetSyncDate
     * store the synchronization date
     * @param syncDate: string
     * @returns Promise<{result: boolean, message: string}>
     */
    setSyncDate(syncDate) {
        this.ensureDatabaseIsOpen();
        try {
            const isTable = this.jsonUtil.isTableExists(this.database, this._isDbOpen, 'sync_table');
            if (!isTable) {
                throw new Error('No sync_table available');
            }
            const syncDateUnixTimestamp = Math.round(new Date(syncDate).getTime() / 1000);
            let stmt = `UPDATE sync_table SET sync_date = `;
            stmt += `${syncDateUnixTimestamp} WHERE id = 1;`;
            const results = this.sqliteUtil.execute(this.database, stmt, false, true);
            if (results.changes < 0) {
                return { result: false, message: 'setSyncDate failed' };
            }
            else {
                return { result: true };
            }
        }
        catch (err) {
            return { result: false, message: `setSyncDate failed: ${err}` };
        }
    }
    /**
     * GetSyncDate
     * store the synchronization date
     * @returns Promise<{syncDate: number, message: string}>
     */
    getSyncDate() {
        this.ensureDatabaseIsOpen();
        try {
            const isTable = this.jsonUtil.isTableExists(this.database, this._isDbOpen, 'sync_table');
            if (!isTable) {
                throw new Error('No sync_table available');
            }
            const syncDate = this.exportToJsonUtil.getSyncDate(this.database);
            if (syncDate > 0) {
                return { syncDate };
            }
            else {
                return { syncDate: 0, message: `setSyncDate failed` };
            }
        }
        catch (err) {
            return { syncDate: 0, message: `setSyncDate failed: ${err}` };
        }
    }
    /**
     * ExecuteSQL
     * execute raw sql statements store in a string
     * @param sql: string
     * @param transaction: boolean
     * @param isSQL92: boolean
     * @returns Promise<number>
     */
    executeSQL(sql, transaction, isSQL92) {
        this.ensureDatabaseIsOpen();
        try {
            if (transaction) {
                const mode = this.sqliteUtil.getJournalMode(this.database);
                console.log(`$$$ in executeSQL journal_mode: ${mode} $$$`);
                this.sqliteUtil.beginTransaction(this.database, this._isDbOpen);
            }
            const results = this.sqliteUtil.execute(this.database, sql, false, isSQL92);
            if (results.changes < 0) {
                throw new Error('ExecuteSQL: changes < 0');
            }
            if (transaction) {
                this.sqliteUtil.commitTransaction(this.database, this._isDbOpen);
            }
            return results.changes;
        }
        catch (executeError) {
            let message = `${executeError}`;
            try {
                if (transaction) {
                    this.sqliteUtil.rollbackTransaction(this.database, this._isDbOpen);
                }
            }
            catch (rollbackErr) {
                message += ` : ${rollbackErr}`;
            }
            throw new Error(`ExecuteSQL: ${message}`);
        }
    }
    /**
     * SelectSQL
     * execute a sql query with/without binding values
     * @param sql: string
     * @param values: string[]
     * @param isSQL92: boolean
     * @returns Promise<any[]>
     */
    selectSQL(sql, values, isSQL92) {
        this.ensureDatabaseIsOpen();
        try {
            const selectResult = this.sqliteUtil.queryAll(this.database, sql, values, isSQL92);
            return selectResult;
        }
        catch (err) {
            throw new Error(`SelectSQL: ${err}`);
        }
    }
    /**
     * runSQL
     * execute a raw sql statement with/without binding values
     * @param statement: string
     * @param values: string[]
     * @param transaction: boolean
     * @param returnMode: string
     * @param isSQL92: boolean,
     * @returns Promise<{changes:number, lastId:number}>
     */
    runSQL(statement, values, transaction, returnMode, isSQL92) {
        this.ensureDatabaseIsOpen();
        try {
            // start a transaction
            if (transaction) {
                const mode = this.sqliteUtil.getJournalMode(this.database);
                console.log(`$$$ in runSQL journal_mode: ${mode} $$$`);
                this.sqliteUtil.beginTransaction(this.database, this._isDbOpen);
            }
        }
        catch (err) {
            throw new Error(`RunSQL: ${err}`);
        }
        try {
            let nStmt = statement;
            if (!isSQL92 && values.length === 0) {
                nStmt = this.sql92Utils.compatibleSQL92(statement);
            }
            const results = this.sqliteUtil.prepareRun(this.database, nStmt, values, false, returnMode);
            if (results.lastId < 0) {
                if (transaction) {
                    this.sqliteUtil.rollbackTransaction(this.database, this._isDbOpen);
                }
                throw new Error(`RunSQL: return LastId < 0`);
            }
            if (transaction) {
                this.sqliteUtil.commitTransaction(this.database, this._isDbOpen);
            }
            return results;
        }
        catch (err) {
            if (transaction) {
                this.sqliteUtil.rollbackTransaction(this.database, this._isDbOpen);
            }
            throw new Error(`RunSQL: ${err}`);
        }
    }
    /**
     * ExecSet
     * execute a set of raw sql statements with/without binding values
     * @param set: any[]
     * @param transaction: boolean,
     * @param returnMode: string,
     * @param isSQL92: boolean,
     * @returns Promise<{changes:number, lastId:number}>
     */
    execSet(set, transaction, returnMode, isSQL92) {
        this.ensureDatabaseIsOpen();
        let results = { changes: 0, lastId: -1 };
        try {
            // start a transaction
            if (transaction) {
                const mode = this.sqliteUtil.getJournalMode(this.database);
                console.log(`$$$ in execSet journal_mode: ${mode} $$$`);
                this.sqliteUtil.beginTransaction(this.database, this._isDbOpen);
            }
        }
        catch (err) {
            throw new Error(`ExecSet: ${err}`);
        }
        try {
            results = this.sqliteUtil.executeSet(this.database, set, false, returnMode, isSQL92);
            if (transaction) {
                this.sqliteUtil.commitTransaction(this.database, this._isDbOpen);
            }
            return results;
        }
        catch (err) {
            const message = err;
            try {
                if (transaction) {
                    this.sqliteUtil.rollbackTransaction(this.database, this._isDbOpen);
                }
            }
            catch (err) {
                throw new Error(`ExecSet: ${message}: ` + `${err}`);
            }
        }
    }
    deleteExportedRows() {
        this.ensureDatabaseIsOpen();
        try {
            this.exportToJsonUtil.delExportedRows(this.database);
            return;
        }
        catch (err) {
            throw new Error(`DeleteExportedRows: ${err}`);
        }
    }
    /**
     * GetTableList
     * get the table's list
     * @returns
     */
    getTableList() {
        this.ensureDatabaseIsOpen();
        try {
            const tableNames = this.sqliteUtil.getTablesNames(this.database);
            return tableNames;
        }
        catch (err) {
            throw new Error(`GetTableList: ${err}`);
        }
    }
    importJson(jsonData) {
        let changes = 0;
        this.ensureDatabaseIsOpen();
        try {
            // set Foreign Keys Off
            this.sqliteUtil.setForeignKeyConstraintsEnabled(this.database, false);
            if (jsonData.tables && jsonData.tables.length > 0) {
                // create the database schema
                changes = this.importFromJsonUtil.createDatabaseSchema(this, jsonData);
                if (changes != -1) {
                    // create the tables data
                    changes += this.importFromJsonUtil.createTablesData(this, jsonData);
                }
            }
            if (jsonData.views && jsonData.views.length > 0) {
                // create the views
                changes += this.importFromJsonUtil.createViews(this, jsonData);
            }
            // set Foreign Keys On
            this.sqliteUtil.setForeignKeyConstraintsEnabled(this.database, true);
            return changes;
        }
        catch (err) {
            throw new Error(`ImportJson: ${err}`);
        }
    }
    exportJson(mode, encrypted) {
        const inJson = {};
        inJson.database = this.dbName.slice(0, -9);
        inJson.version = this.version;
        inJson.encrypted = false;
        inJson.mode = mode;
        this.ensureDatabaseIsOpen();
        try {
            const isTable = this.jsonUtil.isTableExists(this.database, this._isDbOpen, 'sync_table');
            if (isTable) {
                this.exportToJsonUtil.setLastExportDate(this.database, new Date().toISOString());
            }
            else {
                if (inJson.mode === 'partial') {
                    throw new Error(`No sync_table available`);
                }
            }
            let jsonResult = this.exportToJsonUtil.createExportObject(this.database, inJson);
            const keys = Object.keys(jsonResult);
            if (keys.length === 0) {
                const msg = `ExportJson: return Object is empty ` + `No data to synchronize`;
                throw new Error(msg);
            }
            let isValid = this.jsonUtil.isJsonSQLite(jsonResult);
            if (this._encrypted && this._isEncryption && encrypted) {
                jsonResult.overwrite = true;
                jsonResult.encrypted = true;
                const base64Str = this.jsonEncryptUtil.encryptJSONObject(jsonResult);
                jsonResult = {};
                jsonResult.expData = base64Str;
                isValid = true;
            }
            if (isValid) {
                return jsonResult;
            }
            else {
                throw new Error(`ExportJson: retJson not valid`);
            }
        }
        catch (err) {
            throw new Error(`ExportJson: ${err}`);
        }
    }
    /**
     * Throws an error if `this._isDbOpen` is `false`.
     */
    ensureDatabaseIsOpen() {
        if (!this._isDbOpen || !this.database) {
            throw new Error(`getVersion: Database ${this.dbName} is not open yet. You should open it first.`);
        }
    }
}
exports.Database = Database;
//# sourceMappingURL=Database.js.map